import CommentIcon from '@mui/icons-material/Comment'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { Avatar, AvatarGroup, Box, Checkbox, Chip, InputLabel, Stack, Tooltip, Typography } from '@mui/material'
import { getGridStringOperators, GridRenderCellParams } from '@mui/x-data-grid-premium'
import MiniPieChart from 'components/DataGrid/components/MiniPieChart'
import 'components/DataGrid/DataGridContext.css'
import { taskMasterStatusOptions } from 'components/DataGrid/filterArrays'
import { stringAvatar } from 'components/DataGrid/gridOperators'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import { customNotAnyOfOperator } from 'components/DataGrid/operators/isAnyOfOperator'
import { strictEqualsOperator } from 'components/DataGrid/operators/strictEqualsOperator'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import { handleFormatTypesString } from 'components/Forms/types'
import EditableDatePicker from 'components/NCO/EditableDatePicker'
import EditSelectCell from 'components/NCO/EditSelectCell'
import { deriveProgressForChart } from 'components/TeamTasks/progressChartUtils'
import QueueCell from 'components/TeamTasks/QueueCell'
import RenderTaskGridComments from 'components/TeamTasks/RenderTaskGridComments'
import TaskGridOrgs from 'components/TeamTasks/TasksGridOrgs'
import dayjs from 'dayjs'
import { NcoStatusContainer } from 'styles/elements/styleElements'
import EntityAvatar from 'theme/components/Avatar/EntityAvatar'
import { DensityStyleTypes, OrganizationTableItem, Task, User } from 'types'
import { formatDateTime } from 'utils/formatDayjsDateTime'
import Name_Cell from './cells/Name_Cell'

const formatDateString = date => {
  if (!date) return ''
  return dayjs.isDayjs(date) ? date.format('MM/DD/YYYY') : dayjs(date).format('MM/DD/YYYY')
}
const typographyStyles = (densityStyles: DensityStyleTypes) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  padding: densityStyles.padding,
  fontSize: densityStyles.fontSize,
})
const boxRenderStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignContent: 'center',
  justifyContent: 'space-between',
  gap: 2,
  width: '100%',
}
const _defaultWidths = {
  contractedBy: 150,
  orgId: 200,
  name: 250,
  details: 200,
  isPostdated: 75,
  commentCount: 100,
  progressSummary: 100,
  masterStatus: 100,
  taskDays: 100,
  highPrio: 100,
  type: 100,
  startDate: 100,
  endDate: 100,
  assigner: 110,
  assignees: 110,
  queue: 110,
  queueRole: 75,
  createdTime: 75,
  sendReview: 75,
  mode: 75,
  isMarketing: 75,
  isCreative: 75,
  isWebsite: 75,
  website: 75,
  websiteType: 75,
  website_content: 75,
  website_domain_access: 75,
  website_existing_domain: 75,
  website_new_domain: 75,
  website_new_domain_input: 75,
  website_ssl_cert: 75,
}

const renderTeamTasksColumns = (
  organizations: OrganizationTableItem[],
  users: User[],
  context: string,
  handleClickOrganization: (PK: string) => void,
  density: 'compact' | 'standard' | 'comfortable',
  handleTaskClick: (_: any, taskData: Task) => void,
  handleClickEditStart: (params: GridRenderCellParams) => void,
  handleHighPrioChange: (taskId: string, isChecked: boolean) => void,
  selectView: string,
  handlePostdatedChange?: (taskId: string, isChecked: boolean) => void
) => {
  const densityStyles: DensityStyleTypes = getDensityStyles(density)

  return [
    // Column order: Contracted By > Organization > Name > Details
    {
      field: 'contractedBy',
      width: 100,
      headerName: 'Contracted By',
      valueGetter: params => {
        const contractedBy = params && params?.length > 0 ? params?.[0] : {}
        return contractedBy?.name
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        const contractedBy = params?.row?.contractedBy?.length > 0 ? params?.row?.contractedBy?.[0] : {}
        const photoUrl = contractedBy?.photoUrl ?? ''
        const _orgName = typeof params.value === 'string' ? params.value : 'Unknown Organization'

        return (
          <>
            {contractedBy && params?.value ? (
              <>
                {selectView && selectView === 'minimalist' ? (
                  <TaskGridOrgs
                    params={params}
                    onClickOrganization={handleClickOrganization}
                    photoUrl={photoUrl}
                    densityStyles={densityStyles}
                    orgName={params?.value}
                    onTaskClick={handleTaskClick}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      width: '100%',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      gap: 1,
                      pl: 1,
                    }}
                    // onClick={() => handleClickOrganization(contractedBy?.PK)}
                    onClick={event => handleTaskClick(event, params?.row)}
                  >
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                      <Typography
                        noWrap
                        variant="body2"
                        sx={{
                          fontSize: densityStyles.fontSize,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        {params?.value}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography
                  sx={{
                    fontSize: densityStyles.fontSize,
                    color: 'text.disabled',
                    textAlign: 'center',
                  }}
                >
                  —
                </Typography>
              </Box>
            )}
          </>
        )
      },
    },
    {
      field: 'orgId',
      width: 125,
      headerName: 'Organization',
      valueGetter: (_, row) => {
        const { orgId } = row ?? {}
        const arrayOrg =
          organizations.find(org => org.org_uuid === orgId) || organizations.find(org => org.zoho_id === orgId)
        return arrayOrg ? arrayOrg.name : ''
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        const PK = params?.row?.PK
        const orgId = PK?.split('ORG_')?.[1]
        const photoUrl = organizations ? organizations.find(org => org.id === orgId)?.photoUrl : null
        const orgName = typeof params.value === 'string' ? params.value : 'Unknown Organization'

        return (
          <>
            {selectView && selectView === 'minimalist' ? (
              <TaskGridOrgs
                params={params}
                onClickOrganization={handleClickOrganization}
                photoUrl={photoUrl}
                densityStyles={densityStyles}
                orgName={orgName}
                onTaskClick={handleTaskClick}
              />
            ) : selectView && selectView === 'default' ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'left',
                  height: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  maxWidth: '100%',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  padding: densityStyles.padding,
                  gap: densityStyles.gap,
                  pl: 1,
                }}
                // onClick={() => handleClickOrganization(PK)}
                onClick={event => handleTaskClick(event, params?.row)}
              >
                {photoUrl ? (
                  <Avatar
                    src={photoUrl}
                    sx={{
                      width: densityStyles.avatarSize,
                      height: densityStyles.avatarSize,
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                    alt={orgName}
                  />
                ) : (
                  <Box onClick={event => handleTaskClick(event, params?.row)}>
                    <Avatar
                      {...stringAvatar(orgName, densityStyles)}
                      sx={{
                        backgroundColor: 'rgb(114, 229, 218)',
                        width: densityStyles.avatarSize,
                        height: densityStyles.avatarSize,
                        objectFit: 'cover',
                        objectPosition: 'center',
                      }}
                    />
                  </Box>
                )}
                <Typography
                  noWrap
                  variant="body2"
                  sx={{
                    fontSize: densityStyles.fontSize,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {orgName}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'left',
                  height: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                  maxWidth: '100%',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  padding: densityStyles.padding,
                  gap: densityStyles.gap,
                  pl: 1,
                }}
                onClick={() => handleClickOrganization(PK)}
              >
                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                  <Typography
                    noWrap
                    variant="body2"
                    sx={{
                      fontSize: densityStyles.fontSize,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {orgName}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )
      },
    },
    {
      field: 'name',
      width: 100,
      headerName: 'Name',
      renderCell: params => {
        return <Name_Cell params={params} onTaskClick={handleTaskClick} densityStyles={densityStyles} />
      },
    },
    {
      field: 'details',
      width: 115,
      headerName: 'Context',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      valueGetter: (_, row) => row?.details ?? '',
      renderCell: params => {
        const details = params?.value ?? ''
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'left',
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              pl: 1,
            }}
          >
            <Tooltip title={details} placement="top">
              <Typography
                noWrap
                variant="body2"
                sx={{
                  fontSize: densityStyles.fontSize,
                  color: details ? 'text.primary' : 'text.disabled',
                  fontStyle: details ? 'normal' : 'italic',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {details || '—'}
              </Typography>
            </Tooltip>
          </Box>
        )
      },
    },
    {
      field: 'isPostdated',
      width: 45,
      headerName: 'Postdated',
      type: 'boolean',
      align: 'center',
      renderHeader: () => (
        <Tooltip title={<Typography sx={{ fontSize: densityStyles.fontSize }}>Postdated Task</Typography>}>
          <ScheduleIcon sx={{ height: densityStyles.iconSize, width: densityStyles.iconSize }} />
        </Tooltip>
      ),
      valueGetter: (_, row) => {
        // A task is postdated if explicitly set OR if its start date is in the future
        if (row?.isPostdated !== undefined) return row.isPostdated
        if (!row?.startDate) return false
        const startDate = dayjs(row.startDate)
        return startDate.isAfter(dayjs(), 'day')
      },
      renderCell: params => {
        const isPostdated = params.value
        if (params?.row?.isSubtask) return <Box></Box>
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Tooltip title={isPostdated ? 'This task is postdated (click to unmark)' : 'Mark as postdated'}>
              <Checkbox
                checked={!!isPostdated}
                onChange={event => {
                  handlePostdatedChange?.(params.row.SK, event.target.checked)
                }}
                icon={
                  <ScheduleIcon sx={{ height: densityStyles.iconSize, width: densityStyles.iconSize, opacity: 0.3 }} />
                }
                checkedIcon={
                  <ScheduleIcon
                    sx={{
                      height: densityStyles.iconSize,
                      width: densityStyles.iconSize,
                      color: 'text.warning',
                    }}
                  />
                }
                sx={{ p: 0 }}
                inputProps={{ 'aria-label': 'Postdated Task' }}
              />
            </Tooltip>
          </Box>
        )
      },
    },
    {
      field: 'commentCount',
      width: 45,
      headerName: 'Comments',
      renderHeader: () => {
        return (
          <Tooltip title={<Typography sx={{ fontSize: densityStyles.fontSize }}>Comments</Typography>}>
            <CommentIcon sx={{ height: densityStyles.iconSize, width: densityStyles.iconSize }} />
          </Tooltip>
        )
      },
      // flex: 0.3,
      align: 'center', // This centers the column's content
      // headerAlign: 'left', // Optionally, also center the header text
      valueGetter: (_, row) => {
        const count = row?.commentCount ?? 0
        return count
      },
      renderCell: params => {
        const { row } = params
        const taskPK = row?.PK
        const taskSK = row?.SK
        const projectCommentRoute = `/projects/${row?.PK}/${row?.SK}/messages`
        const commentRoute = row?.SK?.startsWith('SUBTASK_')
          ? `/teamtasks/${taskPK}/${row?.parentSK}/messages?task=${taskSK}`
          : context === 'projects'
          ? projectCommentRoute
          : `/teamtasks/${taskPK}/${taskSK}/messages`
        return (
          <RenderTaskGridComments
            params={params}
            boardTheme={'icon.teamtasks'}
            commentRoute={commentRoute}
            densityStyles={densityStyles}
          />
        )
      },
    },
    {
      field: 'progressSummary',
      headerName: 'Progress',
      width: 75,
      valueGetter: params => {
        const s = deriveProgressForChart(params?.row)
        return s.total > 0 ? Math.round((s.done / s.total) * 100) : 0
      },
      renderCell: params => {
        const { row } = params
        const s = deriveProgressForChart(row)

        const chartData = [
          { label: 'done', value: s.done, color: 'rgb(80, 200, 120)' },
          { label: 'inProgress', value: s.inProgress, color: 'rgb(249, 167, 49)' },
          { label: 'needsReview', value: s.needsReview, color: 'rgb(33, 201, 223)' },
          { label: 'needMoreInfo', value: s.needMoreInfo, color: 'rgb(183, 91, 249)' },
          { label: 'notStarted', value: s.notStarted, color: 'rgb(119, 129, 129)' },
          { label: 'onHold', value: s.onHold, color: 'rgb(201, 92, 118)' },
        ].filter(d => d.value > 0)

        const pct = s.total ? Math.round((s.done / s.total) * 100) : 0
        const from =
          s.source === 'summary'
            ? 'Status (legacy)'
            : s.source === 'progress'
            ? 'Checklist (progress)'
            : s.source === 'checklist'
            ? 'Checklist (computed)'
            : 'N/A'

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            {s.total > 0 ? (
              <Tooltip
                title={
                  <Stack sx={{ textAlign: 'left' }}>
                    <Box sx={boxRenderStyle}>
                      <InputLabel sx={{ color: 'text.secondary' }}>Source:</InputLabel>
                      <Typography>{from}</Typography>
                    </Box>
                    <Box sx={boxRenderStyle}>
                      <InputLabel sx={{ color: 'rgb(80, 200, 120)' }}>Done:</InputLabel>
                      <Typography>{s.done}</Typography>
                    </Box>
                    <Box sx={boxRenderStyle}>
                      <InputLabel sx={{ color: 'rgb(249, 167, 49)' }}>In Progress:</InputLabel>
                      <Typography>{s.inProgress}</Typography>
                    </Box>
                    <Box sx={boxRenderStyle}>
                      <InputLabel sx={{ color: 'rgb(33, 201, 223)' }}>Needs Review:</InputLabel>
                      <Typography>{s.needsReview}</Typography>
                    </Box>
                    <Box sx={boxRenderStyle}>
                      <InputLabel sx={{ color: 'rgb(183, 91, 249)' }}>Need More Info:</InputLabel>
                      <Typography>{s.needMoreInfo}</Typography>
                    </Box>
                    <Box sx={boxRenderStyle}>
                      <InputLabel sx={{ color: 'rgb(119, 129, 129)' }}>Not Started:</InputLabel>
                      <Typography>{s.notStarted}</Typography>
                    </Box>
                    <Box sx={boxRenderStyle}>
                      <InputLabel>Total:</InputLabel>
                      <Typography>
                        {s.total} • {pct}%
                      </Typography>
                    </Box>
                  </Stack>
                }
              >
                <div>
                  <MiniPieChart
                    rowId={row.SK}
                    data={chartData}
                    size={densityStyles?.svgSize}
                    strokeWidth={4}
                    context="tasksProgress"
                  />
                </div>
              </Tooltip>
            ) : null}
          </Box>
        )
      },
    },
    {
      field: 'masterStatus',
      width: 100,
      headerName: 'Master Status',
      // flex: 0.6,
      editable: true,
      filterOperators: [
        ...getGridStringOperators(),
        strictEqualsOperator,
        customNotContainsOperator,
        customNotAnyOfOperator,
      ],
      renderCell: params => {
        const _handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center', // vertically center
              justifyContent: 'center', // horizontally center
              width: '100%', // make sure it spans full cell
              height: '100%', // helps in tight rows
              cursor: 'pointer',
            }}
            onClick={() => handleClickEditStart(params)}
          >
            {params?.row?.isSubtask ? (
              <>
                {selectView && selectView === 'textOnly' ? (
                  <Typography sx={{ ...typographyStyles(densityStyles), color: renderColorByString(params.value) }}>
                    {params?.value}
                  </Typography>
                ) : (
                  <Chip
                    size="small"
                    label={params?.value}
                    sx={{
                      fontWeight: 500,
                      width: '100px',
                      textTransform: 'capitalize',
                      bgcolor: renderColorByString(params.value),
                      color: 'common.black',
                      height: 28,
                    }}
                  />
                )}
              </>
            ) : (
              <NcoStatusContainer
                //@ts-ignore
                size="small"
                type={params.value}
                sx={{
                  bgcolor: selectView === 'textOnly' ? '' : renderColorByString(params.value),
                  color: selectView === 'textOnly' ? renderColorByString(params.value) : 'common.black',
                  textAlign: 'center',
                  fontSize: densityStyles.fontSize,
                  px: 1,
                }}
              >
                {params.value}
              </NcoStatusContainer>
            )}
          </Box>
        )
      },
      renderEditCell: params => <EditSelectCell params={params} options={taskMasterStatusOptions} />,
    },
    {
      field: 'taskDays',
      width: 75,
      headerName: 'Task Days',
      // flex: 0.3,
      align: 'center', // This centers the column's content
      // headerAlign: 'left', // Optionally, also center the header text
      valueGetter: params => {
        return params ?? 0
      },
      renderCell: params => (params?.row?.isSubtask ? <Box></Box> : <Box>{params.value}</Box>),
    },
    {
      field: 'highPrio',
      width: 75,
      headerName: 'High Priority',
      // flex: 0.3,
      align: 'center',
      // Only allow inline editing if at most one row is selected
      renderCell: params => {
        return (
          <>
            {params?.row?.isSubtask ? (
              <Box></Box>
            ) : (
              <Checkbox
                checked={!!params.value}
                onChange={event => {
                  handleHighPrioChange(params.row.SK, event.target.checked)
                }}
                inputProps={{ 'aria-label': 'High Priority' }}
              />
            )}
          </>
        )
      },
    },
    {
      field: 'type',
      width: 100,
      headerName: 'Task Type',
      // flex: 0.3,
      editable: false,
      filterOperators: [...getGridStringOperators(), strictEqualsOperator],
      valueGetter: params => {
        return handleFormatTypesString(params)
      },
      renderCell: params => {
        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        const subtaskType = params?.row?.isSubtask ? params?.value : ''
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              // cursor: 'pointer', ///////////////////////
              boxSizing: 'border-box', // Include padding in the size calculation
            }}
            // onClick={() => handleClickEditStart(params)} ///////////////
          >
            {params?.row?.isSubtask ? (
              <Typography
                sx={{
                  color: renderColorByString(params.value),
                  textAlign: 'center',
                  fontSize: 14,
                }}
              >
                {params.value}
              </Typography>
            ) : (
              <NcoStatusContainer
                //@ts-ignore
                size="small"
                type={params.value}
                sx={{
                  bgcolor: selectView === 'textOnly' ? '' : renderColorByString(params.value),
                  textAlign: 'center',
                  fontSize: 14,
                  color: selectView === 'textOnly' ? renderColorByString(params.value) : 'common.black',
                }}
              >
                {params.value}
              </NcoStatusContainer>
            )}
          </Box>
        )
      },
      // renderEditCell: params => <EditSelectCell params={params} options={taskTypeList} />,
    },
    {
      field: 'startDate',
      width: 75,
      editable: true,
      headerName: 'Start Date',
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      renderCell: params => {
        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              cursor: 'pointer',
            }}
            onClick={() => handleClickEditStart(params)}
          >
            {params && params.value ? params.value : ''}
          </Box>
        )
      },
      renderEditCell: params => {
        const handleChange = async newValue => {
          await params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue ? newValue.toISOString() : null, // Save as ISO string
          })
          await params.api.stopCellEditMode({ id: params.id, field: params.field })
        }
        return <EditableDatePicker value={params.value} onChange={handleChange} />
      },
    },
    {
      field: 'endDate',
      width: 75,
      headerName: 'Due Date',
      valueGetter: (_, row) => {
        return row?.endDate ? formatDateString(row?.endDate) : row?.dateDue ? formatDateString(row?.dateDue) : null
      },
      renderCell: params => {
        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
            // onClick={() => handleClickEditStart(params)}
          >
            {params && params.value ? params.value : ''}
          </Box>
        )
      },
      renderEditCell: params => {
        const handleChange = newValue => {
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue ? newValue.toISOString() : null, // Save as ISO string
          })
        }
        return <EditableDatePicker value={params.value} onChange={handleChange} />
      },
    },
    {
      field: 'assigner',
      width: 50,
      headerName: 'Assigner',
      // flex: 0.1,
      // headerAlign: 'center',
      valueGetter: (_, row) => {
        const userId = row?.assigner?.user_uuid ? row?.assigner?.user_uuid : row?.assigner ?? null
        const user = users?.find(u => u?.user_uuid === userId)
        return user?.name ?? null
      },
      renderCell: params => {
        const userId = params?.row?.assigner?.user_uuid
          ? params?.row?.assigner?.user_uuid
          : params?.row?.assigner ?? null
        const user = users?.find(u => u?.user_uuid === userId)
        // const userId =
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {selectView === 'textOnly' ? (
              <Typography sx={{ fontSize: densityStyles.fontSize }}>{user?.name}</Typography>
            ) : (
              <>
                {user ? (
                  <EntityAvatar name={user.name || ''} photoUrl={user.image_512} size="sm" showName={false} />
                ) : (
                  <Avatar sx={{ opacity: 0.1, width: densityStyles.avatarSize, height: densityStyles.avatarSize }} />
                )}
              </>
            )}
          </Box>
        )
      },
    },
    {
      field: 'assigneeNames',
      headerName: 'Assignee Names',
      width: 1,
      hide: true,
      filterable: true,
      valueGetter: (_v, row) => row.assigneeNames ?? '',
    },
    {
      field: 'assignees',
      width: 110,
      headerName: 'Assignee(s)',
      valueGetter: params => {
        const usersArray =
          Array.isArray(params) && params?.length > 0 ? params : !Array.isArray(params) && params ? [params] : []
        return Array.isArray(usersArray) && usersArray?.length > 0
          ? usersArray.map(user => user.name).join(', ')
          : 'No User Name'
      },
      renderCell: params => {
        const userIds = params?.row?.assignees?.map(u => (u?.user_uuid ? u?.user_uuid : u)) ?? []
        const values = users?.filter(u => userIds?.includes(u?.user_uuid)) ?? []
        const isSubtask = params?.row?.isSubtask
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexWrap: 'wrap',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            <AvatarGroup
              max={densityStyles.avatarMax}
              spacing={densityStyles.avatarSpacing}
              slotProps={{
                additionalAvatar: {
                  sx: {
                    height: densityStyles.avatarSize,
                    width: densityStyles.avatarSize,
                    fontSize: densityStyles.fontSize,
                  },
                },
              }}
            >
              {!isSubtask && values.length > 0 ? (
                values.map((user, index) => (
                  // <Tooltip key={`${user.user_uuid || user.id} - ${index}`} title={user?.role}>
                  <Tooltip key={`${user.user_uuid || user.id} - ${index}`} title={user?.role}>
                    <Avatar
                      key={`${user.user_uuid || user.id} - ${index}`}
                      src={user.image_512}
                      sx={{ height: densityStyles.avatarSize, width: densityStyles.avatarSize }}
                    />
                  </Tooltip>
                ))
              ) : isSubtask ? (
                <Typography>–</Typography>
              ) : null}
            </AvatarGroup>
          </Box>
        )
      },
    },
    {
      field: 'queue',
      width: 75,
      headerName: 'Queue',
      valueGetter: (_, row) => {
        const activeAssigneeId =
          typeof row?.queue === 'object' ? row?.queue?.user_uuid : typeof row?.queue === 'string' ? row?.queue : null

        const activeAssignee = activeAssigneeId ? users?.find(u => u?.user_uuid === activeAssigneeId) : null
        return activeAssignee ? activeAssignee?.name : null
      },
      renderCell: (params: GridRenderCellParams) => {
        return <QueueCell params={params} isTextOnly={selectView === 'textOnly'} densityStyles={densityStyles} />
      },
    },
    {
      field: 'queueRole',
      width: 100,
      headerName: 'Queue Role',
      hide: true, // You don’t need to display this column
    },
    {
      field: 'createdTime',
      width: 100,
      headerName: 'Time Created',
      // flex: 0.5,
      // headerAlign: 'center',
      valueGetter: (_, row) => {
        const params = row?.timeCreated || row?.createdTime || null
        if (!params) return null

        // Ensure params.value is a valid date object or ISO string
        const date = dayjs(params) // ✅ Use dayjs to parse
        return date.valueOf()
      },
      // valueGetter: params => {
      //   console.log('paramsdatetime', params ? formatDayJsDateTime(params) : null)
      //   return params ? formatDayJsDateTime(params) : null
      // },
      renderCell: params => {
        const date = params?.value ?? null
        // const dateValue = date ? formatDayjsDateTime(date) : null
        const dateValue = date ? formatDateTime(date) : null
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {dateValue ? dateValue : ''}
          </Box>
        )
      },
      sortComparator: (a, b) => b - a,
    },
    // {
    //   field: 'sendReview',
    //   width: 100,
    //   headerName: '',
    //   sortable: false,
    //   filterable: false,
    //   disableColumnMenu: true,
    //   // flex: 0.3,
    //   align: 'center',
    //   renderCell: params => {
    //     const task = params.row
    //     // if (task.isSubtask) return null

    //     return (
    //       <Tooltip title="Send for Review">
    //         <IconButton
    //           size="small"
    //           onClick={() => handleSendForReview(task)}
    //           sx={{
    //             border: '1px solid',
    //             borderColor: 'divider',
    //             color: 'warning.main',
    //             '&:hover': {
    //               bgcolor: 'warning.light',
    //             },
    //           }}
    //         >
    //           <SendIcon fontSize="small" />
    //         </IconButton>
    //       </Tooltip>
    //     )
    //   },
    // },
    // {
    //   field: 'mode',
    //   width: 100,
    //   headerName: 'Mode',
    //   // flex: 0.3,
    //   renderCell: params => {
    //     const mode = params.row?.mode
    //     if (!mode || mode === 'default') return null

    //     const color = mode === 'review' ? 'warning' : 'default'

    //     return (
    //       <Chip
    //         label={mode}
    //         size="small"
    //         color={color}
    //         variant="outlined"
    //         sx={{ fontWeight: 500, textTransform: 'capitalize' }}
    //       />
    //     )
    //   },
    // },
    {
      field: 'isMarketing',
      width: 100,
      headerName: 'Marketing',
      type: 'boolean',
      hide: true,
    },

    {
      field: 'isCreative',
      width: 100,
      headerName: 'Creative',
      type: 'boolean',
      hide: true,
    },
    {
      field: 'isWebsite',
      width: 100,
      headerName: 'Website',
      type: 'boolean',
      hide: true,
    },
    {
      field: 'website',
      width: 100,
      headerName: 'Website',
      type: 'string',
      // flex: 0.6,
      hide: true,
    },
    {
      field: 'websiteType',
      width: 100,
      headerName: 'Website Type',
      type: 'string',
      // flex: 0.3,
      hide: true,
    },
    {
      field: 'website_content',
      width: 100,
      headerName: 'Website Content',
      type: 'string',
      // flex: 0.3,
      hide: true,
    },
    {
      field: 'website_domain_access',
      width: 100,
      headerName: 'Existing Domain Access',
      type: 'string',
      // flex: 0.3,
      hide: true,
    },
    {
      field: 'website_existing_domain',
      width: 100,
      headerName: 'Existing Domain',
      type: 'string',
      // flex: 0.6,
      hide: true,
    },
    {
      field: 'website_new_domain',
      width: 100,
      headerName: 'New Domain Access',
      type: 'string',
      // flex: 0.3,
      hide: true,
    },
    {
      field: 'website_new_domain_input',
      width: 100,
      headerName: 'New Domain',
      type: 'string',
      // flex: 0.3,
      hide: true,
    },
    {
      field: 'website_ssl_cert',
      width: 100,
      headerName: 'SSL',
      type: 'boolean',
      // flex: 0.3,
      hide: true,
    },
  ].map(col => ({
    ...col,
    //@ts-ignore
    headerAlign: ['left', 'center', 'right'].includes(col.headerAlign) ? col.headerAlign : 'left',
    align: ['left', 'center', 'right'].includes(col.align) ? col.align : 'left',
    type: col.type || undefined,
  }))
}

export default renderTeamTasksColumns
